import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import "@/theme/theme.css";
import "@/i18n"; // initialise i18next once at app boot (Phase 1: sv + en)
import App from "@/App";

// ---------------------------------------------------------------------------
// Suppress the benign "ResizeObserver loop" browser errors. These are emitted
// by the browser (not React) when an observer callback triggers layout that
// schedules another observation in the same frame. They are harmless, but the
// CRA/webpack dev overlay and any global error handler treat them as fatal,
// which crashed the Support form on mobile when opening dropdowns/selects.
// We stop propagation so they never reach those handlers.
const RESIZE_OBSERVER_ERRORS = [
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",
];
function isResizeObserverError(message) {
  return typeof message === "string" && RESIZE_OBSERVER_ERRORS.some((m) => message.includes(m));
}
window.addEventListener(
  "error",
  (e) => {
    if (isResizeObserverError(e?.message)) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  },
  true
);
window.addEventListener("unhandledrejection", (e) => {
  const reason = e?.reason;
  const msg = typeof reason === "string" ? reason : reason?.message;
  if (isResizeObserverError(msg)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});

// ---------------------------------------------------------------------------
// One-time chunk-load recovery. After a new frontend deploy, old hashed JS/CSS
// chunks referenced by a stale shell can 404 — producing a ChunkLoadError and a
// white screen (React never mounts, so the ErrorBoundary can't help). Recover
// ONCE by reloading: the network-first service worker then serves the current
// deployed shell, which references the new bundles. Guarded so it can NEVER
// loop, and scoped strictly to our own /static/ build assets + dynamic-import
// failures — unrelated runtime errors fall through to the ErrorBoundary.
// User session/localStorage/IndexedDB are untouched; this only reloads.
const CHUNK_ERROR_PATTERNS = [
  "Loading chunk",
  "Loading CSS chunk",
  "ChunkLoadError",
  "Importing a module script failed",
  "error loading dynamically imported module",
  "Failed to fetch dynamically imported module",
];
function isChunkLoadError(message) {
  return typeof message === "string" && CHUNK_ERROR_PATTERNS.some((m) => message.includes(m));
}
function recoverFromChunkErrorOnce() {
  try {
    const now = Date.now();
    const last = parseInt(window.sessionStorage.getItem("chunk-reload-at") || "0", 10);
    // Never reload more than once per 20s → guarantees no reload loop.
    if (last && now - last < 20000) return false;
    window.sessionStorage.setItem("chunk-reload-at", String(now));
  } catch (err) {
    // sessionStorage unavailable (private mode): fall back to an in-memory flag.
    if (window.__chunkReloaded) return false;
    window.__chunkReloaded = true;
  }
  window.location.reload();
  return true;
}
function isOwnBuildAsset(url) {
  return typeof url === "string" && url.startsWith(window.location.origin) && url.includes("/static/");
}
window.addEventListener(
  "error",
  (e) => {
    // Failed <script>/<link> loads surface as resource error events (no message).
    const target = e?.target;
    const isAssetTag = target && (target.tagName === "SCRIPT" || target.tagName === "LINK");
    const assetUrl = target?.src || target?.href || "";
    if (isChunkLoadError(e?.message) || (isAssetTag && isOwnBuildAsset(assetUrl))) {
      if (recoverFromChunkErrorOnce()) {
        e.preventDefault();
      }
    }
  },
  true
);
window.addEventListener("unhandledrejection", (e) => {
  const reason = e?.reason;
  const msg = typeof reason === "string" ? reason : reason?.message;
  if (isChunkLoadError(msg)) {
    if (recoverFromChunkErrorOnce()) {
      e.preventDefault();
    }
  }
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Service Worker registration + update detection is handled by
// PwaUpdateProvider (src/context/PwaUpdateContext.jsx).
